import React, { useState, useEffect } from 'react';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

import { Company } from '../../services/adminService';
import { updateCompany } from '../../services/adminService';
import NafCodeSelector from '../shared/NafCodeSelector';
import SelectedNafCodes from '../shared/SelectedNafCodes';

interface CompanyPopupProps {
  company: Company | null;
  isOpen: boolean;
  onClose: () => void;
  onCompanyUpdated: (updatedCompany: Company) => void;
}

export default function CompanyPopup({ company, isOpen, onClose, onCompanyUpdated }: CompanyPopupProps) {
  const [formData, setFormData] = useState<Partial<Company>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        siret: company.siret,
        email: company.email,
        phone: company.phone,
        address: company.address,
        postalCode: company.postalCode,
        city: company.city,
        departement: company.departement,
        country: company.country,
        website: company.website,
        nafCodes: company.nafCodes || [],
      });
    }
  }, [company]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleNafCodesChange = (codes: string[]) => {
    setFormData({
      ...formData,
      nafCodes: codes,
    });
  };

  const handleRemoveNafCode = (codeToRemove: string) => {
    const updatedNafCodes = (formData.nafCodes || []).filter(code => code !== codeToRemove);
    setFormData({
      ...formData,
      nafCodes: updatedNafCodes,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;
    
    setIsSubmitting(true);
    try {
      const updatedCompany = await updateCompany(company.id, formData);
      onCompanyUpdated(updatedCompany);
      showSuccessToast('Entreprise mise à jour avec succès', `company-updated-${updatedCompany.id}`);
      onClose();
    } catch (error) {
      console.error('Error updating company:', error);
      showErrorToast('Erreur lors de la mise à jour de l\'entreprise', `company-update-error-${company.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fermer</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Modifier l'entreprise
                    </Dialog.Title>
                    <div className="mt-4">
                      {company && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="sm:col-span-6">
                              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Nom de l'entreprise
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="name"
                                  id="name"
                                  value={formData.name || ''}
                                  onChange={handleChange}
                                  required
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="siret" className="block text-sm font-medium text-gray-700">
                                SIRET
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="siret"
                                  id="siret"
                                  value={formData.siret || ''}
                                  onChange={handleChange}
                                  required
                                  pattern="[0-9]{14}"
                                  title="Le SIRET doit contenir 14 chiffres"
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                              <p className="mt-1 text-xs text-gray-500">14 chiffres sans espaces</p>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email
                              </label>
                              <div className="mt-1">
                                <input
                                  type="email"
                                  name="email"
                                  id="email"
                                  value={formData.email || ''}
                                  onChange={handleChange}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Téléphone
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="phone"
                                  id="phone"
                                  value={formData.phone || ''}
                                  onChange={handleChange}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                                Adresse
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="address"
                                  id="address"
                                  value={formData.address || ''}
                                  onChange={handleChange}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-2">
                              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                                Code postal
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="postalCode"
                                  id="postalCode"
                                  value={formData.postalCode || ''}
                                  onChange={handleChange}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-2">
                              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                                Ville
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="city"
                                  id="city"
                                  value={formData.city || ''}
                                  onChange={handleChange}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-2">
                              <label htmlFor="departement" className="block text-sm font-medium text-gray-700">
                                Département
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="departement"
                                  id="departement"
                                  value={formData.departement || ''}
                                  onChange={handleChange}
                                  placeholder="ex: 75"
                                  maxLength={3}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                                Pays
                              </label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  name="country"
                                  id="country"
                                  value={formData.country || ''}
                                  onChange={handleChange}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                                Site web
                              </label>
                              <div className="mt-1">
                                <input
                                  type="url"
                                  name="website"
                                  id="website"
                                  value={formData.website || ''}
                                  onChange={handleChange}
                                  placeholder="https://www.exemple.com"
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="nafCodes" className="block text-sm font-medium text-gray-700">
                                Codes NAF
                              </label>
                              <div className="mt-2 space-y-4">
                                <NafCodeSelector
                                  selectedNafCodes={formData.nafCodes || []}
                                  onChange={handleNafCodesChange}
                                  disabled={isSubmitting}
                                  placeholder="Rechercher un code NAF..."
                                  label=""
                                />

                                <SelectedNafCodes 
                                  selectedCodes={formData.nafCodes || []}
                                  onRemove={handleRemoveNafCode}
                                  className="mt-2"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 sm:flex sm:flex-row-reverse">
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                            <button
                              type="button"
                              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                              onClick={onClose}
                            >
                              Annuler
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
} 